import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CFormInput,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowBottom, cilArrowTop, cilColumns, cilDollar, cilSearch } from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import PlayerService from 'src/service/PlayerService'
import PlayerValueTable from './PlayerValueTable'
import Loader from 'src/components/Loader'
import useDebounce from 'src/hooks/useDebounce'

const MOVEMENTS = [
  { key: '', label: 'All Players' },
  { key: 'increased', label: 'Risers' },
  { key: 'decreased', label: 'Fallers' },
  { key: 'no_change', label: 'No Change' },
]

const OPTIONAL_COLUMNS = [
  { key: 'team_name', label: 'Team' },
  { key: 'position_name', label: 'Position' },
  { key: 'trend', label: 'Trend' },
]

const PlayerValue = () => {
  const [loading, setLoading] = useState(true)
  const [activePage, setActivePage] = useState(1)
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(50)
  const [users, setUsers] = useState({})
  const [search, setSearch] = useState('')
  const [teamId, setTeamId] = useState('')
  const [position, setPosition] = useState('')
  const [movement, setMovement] = useState('')
  const [teamList, setTeamList] = useState([])
  const [positionList, setPositionList] = useState([])
  const [visibleColumns, setVisibleColumns] = useState(
    OPTIONAL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}),
  )

  const dSearch = useDebounce(search, 500)
  const dActivePage = useDebounce(activePage, 500)
  const dItemsPerPage = useDebounce(itemsPerPage, 500)

  const columns = [
    { label: 'PID', filter: false, sorter: true, key: 'id', _style: { width: '10%' } },
    { label: 'Image', filter: false, sorter: false, key: 'image', _style: { width: '10%' } },
    { label: 'Full Name', filter: false, sorter: true, key: 'full_name' },
    { label: 'Team', filter: false, sorter: false, key: 'team_name' },
    { label: 'Position', filter: false, sorter: false, key: 'position_name' },
    { label: 'Starting Value', filter: false, sorter: true, key: 'initial_value' },
    { label: 'Current Value', filter: false, sorter: true, key: 'svalue' },
    { label: 'Change', filter: false, sorter: true, key: 'value_change' },
    { label: 'Change (%)', filter: false, sorter: true, key: 'value_change_in_percent' },
    { label: 'Trend', filter: false, sorter: false, key: 'trend' },
  ].filter(
    (col) => !['team_name', 'position_name', 'trend'].includes(col.key) || visibleColumns[col.key],
  )

  useEffect(() => {
    PlayerService.getTeamPositionValueBatBowlStyle().then((result) => {
      setTeamList(result.teamList || [])
      setPositionList(result.position || [])
    })
  }, [])

  useEffect(() => {
    fetchData(dActivePage, dItemsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dActivePage, dItemsPerPage, columnSorter, dSearch, teamId, position, movement])

  const fetchData = (activePage, itemsPerPage) => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    if (dSearch) params.append('full_name', dSearch)
    if (teamId) params.append('team_id', teamId)
    if (position) params.append('position', position)
    if (movement) params.append('movement', movement)
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    PlayerService.playerValues(offset, itemsPerPage, activePage, params).then((result) => {
      setUsers(result.data)
      setLoading(false)
    })
  }

  const handleFilterChange = (setter) => (value) => {
    setActivePage(1)
    setter(value)
  }

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const stats = users?.stats || {}

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <CButton
          color="light"
          className="d-inline-flex p-0 rounded-2 overflow-hidden border"
          style={{ gap: 0 }}
        >
          {MOVEMENTS.map((m) => (
            <span
              key={m.key || 'all'}
              role="button"
              onClick={() => handleFilterChange(setMovement)(m.key)}
              className={`px-3 py-2 small ${
                movement === m.key ? 'bg-primary text-white fw-semibold' : 'text-body'
              }`}
            >
              {m.label}
            </span>
          ))}
        </CButton>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <CInputGroup style={{ maxWidth: 260 }}>
          <CInputGroupText>
            <CIcon icon={cilSearch} />
          </CInputGroupText>
          <CFormInput
            placeholder="Search players"
            value={search}
            onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
          />
        </CInputGroup>
        <CFormSelect
          style={{ maxWidth: 180 }}
          value={teamId}
          onChange={(e) => handleFilterChange(setTeamId)(e.target.value)}
        >
          <option value="">All Teams</option>
          {teamList.map((team) => (
            <option value={team.id} key={team.id}>
              {team.name}
            </option>
          ))}
        </CFormSelect>
        <CFormSelect
          style={{ maxWidth: 180 }}
          value={position}
          onChange={(e) => handleFilterChange(setPosition)(e.target.value)}
        >
          <option value="">All Positions</option>
          {positionList.map((item) => (
            <option value={item.id} key={item.id}>
              {item.name}
            </option>
          ))}
        </CFormSelect>
        <CDropdown alignment="end" className="ms-auto">
          <CDropdownToggle color="secondary" variant="outline">
            <CIcon icon={cilColumns} className="me-1" />
            Columns
          </CDropdownToggle>
          <CDropdownMenu className="p-3">
            {OPTIONAL_COLUMNS.map((col) => (
              <div className="form-check" key={col.key}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`value-col-${col.key}`}
                  checked={!!visibleColumns[col.key]}
                  onChange={() => toggleColumn(col.key)}
                />
                <label className="form-check-label" htmlFor={`value-col-${col.key}`}>
                  {col.label}
                </label>
              </div>
            ))}
          </CDropdownMenu>
        </CDropdown>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <PlayerValueTable
          users={users}
          activePage={activePage}
          setActivePage={setActivePage}
          columnSorter={columnSorter}
          setColumnSorter={setColumnSorter}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          columns={columns}
          loading={loading}
        />
      )}
    </>
  )
}

export default PlayerValue
